import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { Store } from "@ngrx/store";

import { API_BASE_URL } from "../core/api-base-url";
import IConsultantProfile, {
  IProfileUpdateResult,
} from "@skills-app/models/consultant-profile";
import * as NotificationActions from "../notifications/actions/notification.actions";

export type AdminProfileListItem = {
  AzureId: string;
  Email: string;
  DisplayName: string;
  TelstraEmployeeNumber: string | null;
  HasProfile: boolean;
  HasLeft: boolean;
};

export type DuplicateProfileGroup = {
  GroupKey: string;
  MatchReason: string;
  MatchValue: string;
  Profiles: AdminProfileListItem[];
};

@Injectable({ providedIn: "root" })
export class ProfileService {
  private readonly baseUrl = API_BASE_URL;

  /**
   * API_BASE_URL is "/api" in this repo, so the correct endpoint is:
   *   /api/consultantprofile
   */
  private readonly endpoint = `${this.baseUrl}/consultantprofile`;

  private readonly options = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
    withCredentials: true,
  };

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store,
  ) {}

  public getProfileByAzureId(
    azureId: string,
  ): Observable<IConsultantProfile | null> {
    return this.http
      .get<IConsultantProfile>(`${this.endpoint}/${azureId}`, this.options)
      .pipe(catchError((err) => this.handleProfileGetError(err)));
  }

  public getHiddenProfileByAzureId(
    azureId: string,
  ): Observable<IConsultantProfile | null> {
    return this.http
      .get<IConsultantProfile>(
        `${this.endpoint}/hidden/${azureId}`,
        this.options,
      )
      .pipe(catchError((err) => this.handleProfileGetError(err)));
  }

  public getHiddenProfiles(): Observable<AdminProfileListItem[]> {
    return this.http.get<AdminProfileListItem[]>(
      `${this.endpoint}/hidden`,
      this.options,
    );
  }

  public getActiveProfiles(): Observable<AdminProfileListItem[]> {
    return this.http.get<AdminProfileListItem[]>(
      `${this.endpoint}/active`,
      this.options,
    );
  }

  public getDuplicateProfiles(): Observable<DuplicateProfileGroup[]> {
    return this.http.get<DuplicateProfileGroup[]>(
      `${this.endpoint}/duplicates`,
      this.options,
    );
  }

  public hideProfile(azureId: string): Observable<unknown> {
    return this.http.post(`${this.endpoint}/hide/${azureId}`, {}, this.options);
  }

  public unhideProfile(azureId: string): Observable<unknown> {
    return this.http.post(
      `${this.endpoint}/unhide/${azureId}`,
      {},
      this.options,
    );
  }

  public swapDuplicateProfileIdentityFields(
    leftAzureId: string,
    rightAzureId: string,
  ): Observable<unknown> {
    return this.http.post(
      `${this.endpoint}/duplicates/swap-identity-fields`,
      {
        LeftAzureId: leftAzureId,
        RightAzureId: rightAzureId,
      },
      this.options,
    );
  }

  public swapDuplicateProfileIdentityFieldsWithoutEmail(
    leftAzureId: string,
    rightAzureId: string,
  ): Observable<unknown> {
    return this.http.post(
      `${this.endpoint}/duplicates/swap-identity-fields-without-email`,
      {
        LeftAzureId: leftAzureId,
        RightAzureId: rightAzureId,
      },
      this.options,
    );
  }

  public reassignDuplicateProfileData(
    sourceAzureId: string,
    targetAzureId: string,
  ): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(
      `${this.endpoint}/duplicates/reassign-profile-data`,
      {
        SourceAzureId: sourceAzureId,
        TargetAzureId: targetAzureId,
      },
      this.options,
    );
  }

  /**
   * Strict inverse of profile creation. Clears profile data (profile details,
   * projects, education, confidentiality checklist), resets industries and flips
   * HasProfile=false. KEEPS the AzureUser row plus skills, certifications and
   * employment history.
   * Server: DELETE /api/consultantprofile/profile-data/{azureId}
   */
  public deleteProfileData(azureId: string): Observable<string> {
    return this.http.delete(`${this.endpoint}/profile-data/${azureId}`, {
      responseType: "text",
      withCredentials: true,
    });
  }

  /**
   * Full wipe: profile data, skills, certs, employment, memberships and the
   * AzureUser row itself. confirmName must match the display name or the server
   * returns 400. The server also blocks if the user still owns teams.
   * Server: DELETE /api/consultantprofile/profile-and-user/{azureId}?confirmName=...
   */
  public deleteProfileAndUser(
    azureId: string,
    confirmName: string,
  ): Observable<string> {
    const params = new HttpParams().set("confirmName", confirmName ?? "");
    return this.http.delete(
      `${this.endpoint}/profile-and-user/${azureId}`,
      {
        params,
        responseType: "text",
        withCredentials: true,
      },
    );
  }

  /**
   * Backward-compatible wrapper.
   *
   * Historically named getProfile(email). However, the backend endpoint is GUID-based.
   * To avoid breaking unknown callers, we keep this method but make it safe:
   * - If the argument looks like a GUID => call getProfileByAzureId
   * - Otherwise => return null
   */
  public getProfile(
    emailOrAzureId: string,
  ): Observable<IConsultantProfile | null> {
    if (this.isGuid(emailOrAzureId)) {
      return this.getProfileByAzureId(emailOrAzureId);
    }
    return of(null);
  }

  public getProfileBySecret(
    secret: string,
  ): Observable<IConsultantProfile | null> {
    return this.http
      .get<IConsultantProfile>(
        `${this.endpoint}/secret/${secret}`,
        this.options,
      )
      .pipe(catchError((err) => this.handleProfileGetError(err)));
  }

  public getCurrentUserProfile(): Observable<IConsultantProfile | null> {
    return this.http
      .get<IConsultantProfile>(this.endpoint, this.options)
      .pipe(catchError((err) => this.handleProfileGetError(err)));
  }

  public getUserProfileStatus(
    azureId: string,
  ): Observable<IProfileUpdateResult | null> {
    return this.http
      .get<IProfileUpdateResult>(
        `${this.endpoint}/status/${azureId}`,
        this.options,
      )
      .pipe(catchError((err) => this.handleStatusGetError(err)));
  }

  public updateProfile(
    profile: IConsultantProfile,
  ): Observable<IProfileUpdateResult> {
    return this.http.post<IProfileUpdateResult>(
      this.endpoint,
      profile,
      this.options,
    );
  }

  public downloadProfile(
    email: string,
    templateType: string,
  ): Observable<Blob> {
    const safeEmail = encodeURIComponent(email);
    const safeType = encodeURIComponent(templateType);

    return this.http.get(
      `${this.endpoint}/docx/${safeEmail}?profileType=${safeType}`,
      {
        responseType: "blob",
        withCredentials: true,
      },
    );
  }

  public searchProfile(namePrefix: string): Observable<unknown> {
    return this.http.get(`${this.endpoint}/search/${namePrefix}`, this.options);
  }

  private isGuid(value: string): boolean {
    return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(
      value,
    );
  }

  private handleProfileGetError(
    err: unknown,
  ): Observable<IConsultantProfile | null> {
    const e = err as HttpErrorResponse;

    if (e?.status === 404) {
      this.store.dispatch(
        NotificationActions.snackbarOpen({
          message: "Profile not found",
          action: "Dismiss",
        }),
      );
      return of(null);
    }

    if (e?.status === 401) {
      return of(null);
    }

    return of(null);
  }

  private handleStatusGetError(
    err: unknown,
  ): Observable<IProfileUpdateResult | null> {
    const e = err as HttpErrorResponse;

    if (e?.status === 401 || e?.status === 404) {
      return of(null);
    }

    return throwError(() => err);
  }
}
